import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { requestSmsCaptchaToken } from '../../components/captcha/requestSmsCaptchaToken';

function useCountDown(ms = 60): [() => void, number] {
  const [time, setTime] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const start = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setTime(ms);
    timerRef.current = setInterval(() => {
      setTime((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current!);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  return [start, time];
}

interface SendCodeButtonProps {
  disabled?: boolean;
  mobile: string;
  countryCode: string;
  doSendCode: (captchaToken: string) => Promise<any>;
}

export default function SendCodeButton({ disabled = false, doSendCode, mobile, countryCode }: SendCodeButtonProps) {
  const [t] = useTranslation();
  const [loading, setLoading] = useState(false);
  const [countDown, time] = useCountDown(60);

  const handleSend = useCallback(async () => {
    setLoading(true);
    try {
      const captchaToken = await requestSmsCaptchaToken({ mobile, countryCode });
      await doSendCode(captchaToken);
      countDown();
    } finally {
      setLoading(false);
    }
  }, [doSendCode, countDown, mobile, countryCode]);

  const isDisabled = loading || disabled || time > 0;

  return (
    <button
      type="button"
      onClick={handleSend}
      disabled={isDisabled}
      className={cn(
        'tw-shrink-0 tw-w-28 tw-h-11 tw-rounded-md tw-text-sm tw-font-medium',
        'tw-border tw-transition-colors tw-inline-flex tw-items-center tw-justify-center tw-gap-1.5',
        isDisabled
          ? 'tw-opacity-50 tw-cursor-not-allowed'
          : 'tw-cursor-pointer hover:tw-bg-cyan-400/10 hover:tw-text-cyan-300'
      )}
      style={{
        borderColor: 'rgba(34, 211, 238, 0.5)',
        color: 'rgb(34, 211, 238)',
        backgroundColor: 'transparent',
      }}
    >
      {loading && <Loader2 className="tw-h-4 tw-w-4 tw-animate-spin" />}
      {time > 0 ? `${time}s` : t('login.verificationCode', '获取验证码')}
    </button>
  );
}
